import type { ContactCreate } from "@/canvassFlow/types";
import { contactsGateway } from "@/gateways/contactsGateway";
import type { MutationCallbacks } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { ref, type Ref } from "vue";
import type { Contact, ContactListItem } from "./types";

export function useNewContact(houseId: number) {
  const contact: Ref<ContactCreate> = ref({
    id: window.crypto.randomUUID(),
    name: "",
    salutation: null,
    houseId,
    apartment: null,
    emailAddress: null,
    phoneNumber: null,
    pendingEmailAddress: null,
    pendingPhoneNumber: null,
    languages: null,
    isHouseSpeaker: false,
    isPotentialCoreActivist: false,
    hasNoInterestInBecomingActive: false,
    isTenantAssociationMember: false,
    hasLegalProtectionInsurance: false,
    consent: {
      consentText: "",
      signatureSvg: "",
    },
  });

  return { contact };
}

export function useGetContacts(houseId?: number) {
  const query = useQuery({
    queryKey: ["contacts", houseId],
    queryFn: () => contactsGateway.listContacts(houseId),
  });

  return { ...query, getContactsStatus: query.status, contacts: query.data };
}

export function useGetContact(id: string) {
  const query = useQuery({
    queryKey: ["contact", id],
    queryFn: () => contactsGateway.getContact(id),
  });

  return { ...query, getContactStatus: query.status, contact: query.data };
}

export function useUpdateContact(houseId?: number) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (contact: Contact) => contactsGateway.updateContact(contact),
    onSuccess: (contact: Contact) => {
      queryClient.invalidateQueries({ queryKey: ["contacts", houseId] });
      queryClient.setQueryData(["contact", contact.id], contact);
    },
  });

  return {
    ...mutation,
    updateContactStatus: mutation.status,
    updateContact: mutation.mutate,
  };
}

export function useCreateContact(
  houseId?: number,
  callbacks: MutationCallbacks<Contact> = {},
) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (contact: ContactCreate) =>
      contactsGateway.createContact(contact),
    onSuccess: (contact: Contact) => {
      queryClient.invalidateQueries({ queryKey: ["contacts", houseId] });
      queryClient.setQueryData(["contact", contact.id], contact);

      if (callbacks.onSuccess) callbacks.onSuccess(contact);
    },
  });

  return {
    ...mutation,
    createContactStatus: mutation.status,
    createContact: mutation.mutate,
  };
}

export function useDeleteContact(houseId?: number) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (contact: Contact | ContactListItem) =>
      contactsGateway.deleteContact(contact),
    onSuccess: (_, contact) => {
      queryClient.invalidateQueries({ queryKey: ["contacts", houseId] });
      queryClient.invalidateQueries({ queryKey: ["contact", contact.id] });
    },
  });

  return {
    ...mutation,
    resetDeleteContact: mutation.reset,
    deleteContactStatus: mutation.status,
    deleteContact: mutation.mutate,
  };
}
